<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)">
        <UCard :ui="{ rounded: 'rounded-lg' }">
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Documents</h3>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="$emit('update:open', false)" />
                </div>
            </template>

            <div class="space-y-4">
                <label
                    v-if="canEdit"
                    class="flex flex-col items-center justify-center gap-2 p-6 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:border-gray-400"
                    @dragover.prevent
                    @drop.prevent="onDrop"
                >
                    <UIcon name="i-ph-upload-simple-duotone" class="size-6 text-gray-500" />
                    <span class="text-sm text-gray-600">Drop files or click to upload</span>
                    <span class="text-xs text-gray-400">JPG, PNG, PDF · max 10 MB</span>
                    <input
                        type="file"
                        class="hidden"
                        multiple
                        accept="image/jpeg,image/png,application/pdf"
                        :disabled="uploading"
                        @change="onPick"
                    />
                </label>

                <div v-if="uploading" class="flex items-center gap-2 text-sm text-gray-600">
                    <UIcon name="i-ph-spinner" class="size-4 animate-spin" /> Uploading…
                </div>

                <div v-if="loading" class="py-6 flex justify-center">
                    <UIcon name="i-ph-spinner" class="size-5 animate-spin text-gray-400" />
                </div>
                <p v-else-if="!documents.length" class="py-6 text-center text-sm text-gray-400 italic">No documents.</p>
                <ul v-else class="divide-y divide-gray-100">
                    <li v-for="doc in documents" :key="doc.id" class="flex items-center gap-3 py-2.5">
                        <UIcon :name="iconFor(doc.mime_type)" class="size-5 text-gray-500 shrink-0" />
                        <div class="min-w-0 flex-1">
                            <p class="text-sm text-gray-800 truncate">{{ doc.file_name }}</p>
                            <p class="text-xs text-gray-400">{{ formatSize(doc.size_bytes) }} · {{ dayjs(doc.created_at).format('MMM D, YYYY HH:mm') }}</p>
                        </div>
                        <UButton size="xs" color="gray" variant="ghost" icon="i-ph-eye" :loading="viewingId === doc.id" @click="view(doc)" />
                        <UButton v-if="canEdit" size="xs" color="red" variant="ghost" icon="i-ph-trash" :loading="deletingId === doc.id" @click="remove(doc)" />
                    </li>
                </ul>
            </div>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import type { CallReport } from '~/types/callReport'
import type { CallReportDocument } from '~/types/callReportDocument'
import { UserRole } from '~/types/user'
import { useReportDocuments } from '~/composables/useReportDocuments'

const props = defineProps<{
    open: boolean
    report: CallReport
}>()

const emit = defineEmits<{ 'update:open': [boolean]; 'changed': [number] }>()

const auth = useAuthStore()
const toast = useToast()
const { listDocuments, uploadDocument, getSignedUrl, deleteDocument } = useReportDocuments()

const documents = ref<CallReportDocument[]>([])
const loading = ref(false)
const uploading = ref(false)
const viewingId = ref<string | null>(null)
const deletingId = ref<string | null>(null)

const canEdit = computed(() =>
    auth.user?.id === props.report.user_id || auth.hasRole(UserRole.medicalDirector)
)

watch(() => props.open, (isOpen) => {
    if (isOpen) load()
})

async function load() {
    loading.value = true
    try {
        documents.value = await listDocuments(props.report.id)
    } catch (error: any) {
        toast.add({ title: 'Could not load documents', description: error.message, color: 'red' })
    } finally {
        loading.value = false
    }
}

async function handleFiles(files: File[]) {
    if (!files.length || !auth.user?.id) return
    uploading.value = true
    let changed = false
    for (const file of files) {
        try {
            await uploadDocument(props.report.id, auth.user.id, file)
            changed = true
        } catch (error: any) {
            toast.add({ title: 'Upload failed', description: error.message, color: 'red' })
        }
    }
    uploading.value = false
    if (changed) {
        await load()
        emit('changed', documents.value.length)
    }
}

function onPick(event: Event) {
    const input = event.target as HTMLInputElement
    handleFiles(Array.from(input.files ?? []))
    input.value = ''
}

function onDrop(event: DragEvent) {
    handleFiles(Array.from(event.dataTransfer?.files ?? []))
}

async function view(doc: CallReportDocument) {
    viewingId.value = doc.id
    try {
        const url = await getSignedUrl(doc.storage_path)
        window.open(url, '_blank', 'noopener')
    } catch (error: any) {
        toast.add({ title: 'Could not open document', description: error.message, color: 'red' })
    } finally {
        viewingId.value = null
    }
}

async function remove(doc: CallReportDocument) {
    if (!confirm(`Delete ${doc.file_name}?`)) return
    deletingId.value = doc.id
    try {
        await deleteDocument(doc)
        documents.value = documents.value.filter((d) => d.id !== doc.id)
        emit('changed', documents.value.length)
    } catch (error: any) {
        toast.add({ title: 'Delete failed', description: error.message, color: 'red' })
    } finally {
        deletingId.value = null
    }
}

function iconFor(mime: string) {
    return mime === 'application/pdf' ? 'i-ph-file-pdf-duotone' : 'i-ph-file-image-duotone'
}

function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}
</script>
